import React from 'react';
import { isNil } from '@benzinga/utils';
import styled from '@benzinga/themetron';
import { getPerformanceDisplay } from '../../utils/formatters';
import { Up } from '@benzinga/themed-icons';

export interface Props {
  decimalPlaces?: number | undefined;
  hasArrow?: boolean;
  isPercentage?: boolean;
  label?: string;
  onClickHandler?: (event?: React.SyntheticEvent<HTMLSpanElement>) => void;
  stat: number | null | undefined;
  suppressPlusSign?: boolean;
}

const statSign = (stat: string | number | null | undefined) => Math.sign(Number(stat));

const getColorClassAndArrow = (
  stat: string | number | null | undefined,
): {
  Arrow: typeof UpArrow | typeof DownArrow | null;
  type: 'negative' | 'positive' | 'neutral';
} => {
  switch (statSign(stat)) {
    case 1: // positive, true
      return {
        Arrow: UpArrow,
        type: 'positive',
      };
    case -1: // negative
      return {
        Arrow: DownArrow,
        type: 'negative',
      };
    default:
      return {
        Arrow: null,
        type: 'neutral',
      };
  }
};

export const PerformanceColor: React.FC<Props> = React.memo(props => {
  const { decimalPlaces, hasArrow, isPercentage, label, onClickHandler, stat, suppressPlusSign } = props;

  const { Arrow, type } = getColorClassAndArrow(stat);
  const display = getPerformanceDisplay(label, stat, isPercentage, suppressPlusSign, decimalPlaces);

  const RenderType = React.useMemo(
    () =>
      display ? (
        <Span onClick={onClickHandler} type={type}>
          {hasArrow ? Arrow ? <Arrow /> : null : null}
          {display}
        </Span>
      ) : null,
    [Arrow, display, hasArrow, onClickHandler, type],
  );

  if (isNil(stat)) {
    if (isNil(label)) {
      return null;
    } else {
      return (
        <Span onClick={onClickHandler} type="neutral">
          {label}
        </Span>
      );
    }
  }

  return RenderType;
});

export const PerformanceColorV2: React.FC<Props> = React.memo(props => {
  const { decimalPlaces, hasArrow, isPercentage, label, onClickHandler, stat, suppressPlusSign } = props;

  const { Arrow, type } = getColorClassAndArrow(stat);
  const display = getPerformanceDisplay(label, stat, isPercentage, suppressPlusSign, decimalPlaces);

  const RenderType = React.useMemo(
    () =>
      display ? (
        <SpanV2 className="spanv2" onClick={onClickHandler} type={type}>
          {hasArrow ? Arrow ? <Arrow /> : null : null}
          {display}
        </SpanV2>
      ) : null,
    [Arrow, display, hasArrow, onClickHandler, type],
  );

  if (isNil(stat)) {
    if (isNil(label)) {
      return null;
    } else {
      return (
        <SpanV2 onClick={onClickHandler} type="neutral">
          {label}
        </SpanV2>
      );
    }
  }

  return RenderType;
});

PerformanceColor.displayName = 'PerformanceColor';

const Arrow = styled(Up)`
  font-size: 0.8em;
  margin-right: 0.4em;
  margin-top: 1px;
  margin-bottom: -1px;
`;

const UpArrow = styled(Arrow)`
  fill: ${props => props.theme.colors.statistic.positive};
`;

const DownArrow = styled(Arrow)`
  transform: rotate(180deg);
  fill: ${props => props.theme.colors.statistic.negative};
`;

const Span = styled.span<{ type: 'negative' | 'positive' | 'neutral' }>`
  color: ${props => props.theme.colors.statistic[props.type]};
  fill: ${props => props.theme.colors.statistic[props.type]};
`;

const SpanV2 = styled.span<{ type: 'negative' | 'positive' | 'neutral' }>`
  color: ${props => props.theme.colors.statistic[props.type]};
  fill: ${props => props.theme.colors.statistic[props.type]};
  padding: 1px 6px;
  justify-content: center;
  align-items: center;
  background-color: #373f49;
  border-radius: 4px;
  margin: 2px 0px;
  min-width: 50px;
  font-weight: 600;
  font-size: 13px;
  font-family: 'Nimbus Sans L', Helvetica;
`;
